// @flow

import * as React from 'react';
import {useContext, useMemo, useReducer, useEffect} from 'react';
import {contextStubFunction} from 'docs/util';
import {useAccount, useArticle, useApi} from 'docs/data';
import type {VideoModel} from './Videos';
import type {VideosResolvers} from './VideosResolvers';
import {createResolvers} from './VideosResolvers';
import {VideosReducer} from './VideosReducer';
import {loadAllVideos} from './actions/loadAllVideos';

export type VideosContextType = {
  ...VideosResolvers,

  // this is a global loading variable for any videos fetch
  isLoading: boolean,

  needsVideosToBeFetched: boolean,

  loadAll: () => Promise<any>,

  tocVideo: ?VideoModel,

  videoPlayerPosition: ?ClientRect,
  setVideoPlayerPosition: (
    ?(((?ClientRect) => ?ClientRect) | ClientRect),
  ) => void,
};

const VideosContext = React.createContext<VideosContextType>({
  isLoading: false,
  needsVideosToBeFetched: true,

  getVideo: contextStubFunction('Video'),
  getTopic: contextStubFunction('Video'),
  getPlaylist: contextStubFunction('Video'),
  getPlacement: contextStubFunction('Video'),
  getAll: contextStubFunction('Video'),
  loadAll: contextStubFunction('Video'),
  getVideoOrTopic: contextStubFunction('Video'),

  tocVideo: undefined,
  videoPlayerPosition: undefined,
  setVideoPlayerPosition: contextStubFunction('Video'),
});

type VideosProviderProps = {
  children?: React.Node,
};

export const VideosProvider = (props: VideosProviderProps) => {
  const {api} = useApi();

  // TODO: in the future we will control playback and PiP from here

  const [
    videoPlayerPosition,
    setVideoPlayerPosition,
  ] = React.useState<?ClientRect>();

  const [state, dispatch] = useReducer(VideosReducer, {
    isLoading: false,
  });

  const videosData = useMemo(() => {
    const {allVideos, isLoading, error} = state;
    const resolvers = createResolvers(allVideos);

    // we need to fetch videos if:
    // 1. we don't have all videos yet AND
    // 2. we are not currently loading the videos AND
    // 3. there is not an error
    const needsVideosToBeFetched = !allVideos && !isLoading && !error;

    return {
      ...resolvers,
      loadAll: loadAllVideos(api, dispatch),
      isLoading,
      needsVideosToBeFetched,
    };
  }, [api, state]);

  const {article, isLoading: isArticleLoading} = useArticle();
  const {account} = useAccount();

  const canShowVideoPlayer =
    account.flags && account.flags.docs_video_above_toc;

  const tocVideo =
    article.videos &&
    article.videos.video &&
    canShowVideoPlayer &&
    !isArticleLoading
      ? article.videos.video
      : undefined;

  return (
    <VideosContext.Provider
      value={{
        ...videosData,
        tocVideo,
        videoPlayerPosition,
        setVideoPlayerPosition,
      }}
    >
      {props.children}
    </VideosContext.Provider>
  );
};

export type VideosContextProps = {|
  prefetchAllVideos: ?boolean,
|};

export const useVideos = ({
  prefetchAllVideos = false,
}: VideosContextProps = {}): VideosContextType => {
  const context = useContext(VideosContext);

  useEffect(() => {
    if (prefetchAllVideos && context.needsVideosToBeFetched) {
      context.loadAll();
    }
  }, [prefetchAllVideos, context]);

  return context;
};
